/**
 * Full email document assembly — ported verbatim from the legacy esExport()
 * blank-template branch (MSO conditionals, dark-mode meta, 600px column,
 * Gmail-clip-safe head). Link aliasing (esAliasLinks) is a send-time concern
 * and stays in the legacy pipeline / backend.
 */
import { FULL_REGISTRY } from "./blocks";

/**
 * Where a block came from when the document was ASSEMBLED FROM A TEMPLATE
 * (monolith `m.__ech`, set by `echDoc()` L8855).
 *
 * Declared here rather than in services/echTemplates.ts so the document model
 * has no dependency on the ECH layer. `real` is false when the SFMC block itself
 * hasn't been pulled and the canvas is showing an approximate Studio twin — the
 * distinction the monolith keeps so a preview is never mistaken for Layne's
 * artwork. `id` is the SFMC ContentID, and it is the key `echRebuildKeepEdits`
 * matches on to carry a user's edits across a rebuild (audit row 20).
 */
export interface EchPlacement {
  /** the SFMC block name, e.g. `ECH_BlueHeader` */
  name: string;
  /** the SFMC ContentID */
  id: string;
  /** 1-based position in the template */
  pos: number;
  /** `sfmc` (block-declared placement) | `sfmc-template-block` (ECH_TPL_* order) */
  src: string;
  /** the real SFMC block is on the canvas, not a twin or a placeholder */
  real: boolean;
  /** a Studio twin is standing in for artwork that lives in Content Builder */
  approx: boolean;
  /**
   * WHICH TEMPLATE THIS DOCUMENT WAS ASSEMBLED FROM, AND ON WHICH MARKUP.
   *
   * The stamp rides on the document rather than in a component ref because that is
   * the only place that survives a Studio close/reopen — and a mount-scoped ref was
   * exactly why `echRebuildKeepEdits` was dead code (code review 2026-08-19 #4): on
   * every mount the ref was null, so the "fresh assembly" branch always won and the
   * carry branch was unreachable. In the monolith this state is implicit, because
   * `doc` and `curTpl` are page globals that outlive any panel.
   *
   * `ver` is the SFMC markup version the blocks were built from (echVersion() +
   * echTplVersion()). A LATER pull bumps it, and that difference — same template,
   * newer markup — is precisely the condition for carrying the user's edits forward
   * instead of rebuilding from block defaults.
   */
  tpl?: string;
  ver?: number;
}

export interface AnyBlockInstance {
  id: string;
  type: string;
  data: Record<string, unknown>;
  /** set only on a block placed by template assembly (`echDoc`) */
  __ech?: EchPlacement;
}

/* ── REAL DARK MODE, IN THE HEAD (2026-09-02) ──
   History: this head declared `light dark` with no dark styling (Jie's LTGME-627/628:
   unreadable mixed renders), then went light-only on 2026-08-31 as the honest fix.
   Now it declares `light dark` again AND styles both, from the head alone — the 23
   ECH blocks stay light-only and untouched. Under prefers-color-scheme:dark the
   attribute selectors remap the block palette: white canvas -> #10192F, cream
   #F0EDE0 panels -> Ocean #203F7C, grey #F2F2F2 -> Midnight #1B2A58, all text -> white,
   navy pill buttons -> Cobalt #005DAA, and the white pills on the Cobalt Grand
   Opening panel stay white with Cobalt text. Monochrome navy PNGs (the header logo
   by asset id, anything carrying class="dm-inv") flip to white via a CSS filter, so
   no dark image assets are hosted. Measured in Litmus on the Coming Soon and Launch
   templates (emails 22650267 / 22650760): Apple Mail 16, iPhone 16/17 and iPad dark
   render fully on-palette with zero leftover white boxes; the light render is
   byte-identical to before. Gmail (Android/iOS) and Outlook ignore this query and
   force-invert their own way, exactly as they did with the light-only head —
   compared capture-for-capture, no regression there.
   Two traps, both measured: (1) Apple Mail remaps a pure #FFFFFF background to its own
   dark grey even inside a supported dark scheme — use #FEFEFE for anything that must
   stay white in dark; (2) a `background` SHORTHAND !important in a lower-specificity
   rule still beats a `background-color` !important in a higher one on some clients —
   set both longhand and shorthand when overriding.

   LITMUS REVIEW, PORT ORCHARD COMING SOON (2026-09-04) — three dark-mode findings,
   answered here:
   · the navy section bar (ECH_BlueHeader, bgcolor #1B2A58, no radius) sat on the
     #10192F canvas at almost no contrast → it is now Ocean #203F7C in dark, like the
     cream panels; the `:not([style*="border-radius"])` keeps the navy pill buttons on
     their own Cobalt rule.
   · `brightness(0) invert(1)` crushes a two-tone headline image and a white-filled
     line icon to a solid white shape ("sticker", "blob"). A filter cannot recover two
     tones or a fill, so the durable fix is a DARK VARIANT of the asset, swapped by
     class: `.dm-only` (hidden in light, shown in dark) beside `.dm-off` (shown in
     light, hidden in dark). The filter stays as the fallback for an asset that has no
     variant yet — a white shape beats invisible navy on navy.
   · images-off: alt text was unstyled browser default. `img` now carries the brand
     colour/face in light and white in dark, so a missing image reads as copy, not as
     a broken page. WHAT alt to say is the block's (Layne: headline image alt, a
     decorative "" on the house icon, footer logo + social alts).

   CEDAR POINT REVIEW (2026-09-15) — the Community Home Table's COLUMN HEADERS are
   `<th>` (Layne's `table.border thead th{color:#303030}`), and the text remap named
   `td` but never `th`, so in dark the four headers ("Homesite / Plan", "Sq Ft / Beds /
   Baths", "Price", "View") sat #303030 on #10192F. Measured in Chrome with the media
   query forced: every body cell white, the header row invisible. `th` is in the list
   now, and its 2px #303030 rule goes white too. ECH_Blank_Template (SFMC 1029913,
   was ECH_Shell) carries the same bytes after its ECH_HEAD comment — re-sync it in
   the same session as ANY edit to this string.

   SFMC WRAPS LONG LINES — ONE RULE PER LINE, NOTHING NEAR 998 CHARS (2026-09-17).
   Jie's Litmus 22800551 was sent THROUGH SFMC and carried the v3.3 head with both
   metas and the :root pair, yet Apple Mail still rendered its own auto-dark (near-black
   canvas, copy left #303030, navy buttons). The received source showed why: SFMC's
   mailer breaks any line over ~780 chars at a space (RFC 5322's 998-char limit), and
   this <style> was ONE 3,234-char line — three of the four breaks landed inside quoted
   attribute selectors (`[style*="background:⏎ #FFFFFF"]`). A raw newline in a CSS
   string is a bad-string token, every later `"` flips quote parity, and the rest of the
   dark block is garbage. My Litmus PASTE test (22799753) never went through the mailer,
   which is why it "proved" the metas fix. So: every rule on its own line, longest line
   well under 600, no quoted string across a break — darkMode.test.ts pins all three.
   The same rule applies to ECH_Blank_Template's copy of this head and to any long
   inline style in a block. Proof of a dark-mode change is a Litmus run that arrived
   via an SFMC send, never a paste.

   THE MAIN IMAGE'S PINNED HEIGHT LETS GO ON PHONES (2026-09-17 PM, Layne's two-block cut).
   ECH_MainImage / ECH_MainImage600 write the chosen height INTO the inline style
   (`height:{{imgHeight}}px`) so the dropdown is visible in every client; the cost is a
   squashed photo on a narrow screen, where max-width:100% shrinks the width but the pinned
   height stays. `.es-main-img` on the img (Layne adds the class) gets width:100% +
   height:auto back under 600px — in the HEAD, because Gmail strips block CSS. The same
   line lives in ECH_Blank_Template v3.5.

   APPLE MAIL NEEDS BOTH DECLARATIONS (2026-09-17): `supported-color-schemes` joins
   `color-scheme` on `:root`, and both metas lead the head. Without the pair Apple
   Mail treats the email as dark-unaware and auto-adjusts it itself.

   ALSO HERE, NOT IN THE BLOCKS: the 480px stacking rules (.stack-*, .go-stack-*,
   .pc-stack-*). The ECH blocks carry these in <style> tags inside their own BODY
   markup, and several clients (Gmail app for non-Gmail accounts among them) strip
   body styles — which is Jie's "broken on mobile / cards don't stack" family. Head
   styles survive where body styles don't, so the canonical copy of every block's
   mobile rule lives in this head; the block-body copies stay as a fallback for
   contexts that render a block outside this shell. New block classes with mobile
   behaviour must be added here too. */
const HEAD =
  '<!--[if !mso]><!--><meta http-equiv="X-UA-Compatible" content="IE=edge"><!--<![endif]--><meta http-equiv="Content-Type" content="text/html; charset=UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="color-scheme" content="light dark"><meta name="supported-color-schemes" content="light dark"><style type="text/css">\n:root{color-scheme:light dark;supported-color-schemes:light dark}\n#outlook a{padding:0}\nbody{margin:0;padding:0;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%}\ntable,td{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt}\nimg{border:0;height:auto;line-height:100%;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;color:#1B2A58;font-family:Arial,Helvetica,sans-serif;font-size:13px}\np{margin:0}\n.dm-only{display:none;max-height:0;overflow:hidden;mso-hide:all}\n@media only screen and (max-width:600px){\n.es-w{width:100%!important}\n.es-main-img{width:100%!important;height:auto!important}\n}\n@media screen and (max-width:480px){\n.stack-column,.go-stack-column{display:block!important;width:100%!important;text-align:center!important;padding:0!important}\n.stack-button-container,.go-stack-button-container{margin:0 auto 8px auto!important}\n.pc-stack-column,.pc-stack-column-img{display:block!important;width:100%!important;padding-bottom:20px!important}\n.pc-stack-column-img img{width:100%!important;height:auto!important}\n.pc-stack-column-copy{padding-left:0!important;padding-top:0!important}\n}\n@media (prefers-color-scheme:dark){\nbody,.es-bg,table[bgcolor="#FFFFFF"],td[bgcolor="#FFFFFF"],table[bgcolor="#ffffff"],td[bgcolor="#ffffff"],[style*="background-color: #FFFFFF"],[style*="background-color:#FFFFFF"],[style*="background-color: #ffffff"],[style*="background:#ffffff"],[style*="background: #FFFFFF"]{background-color:#10192F!important;background:#10192F!important}\n[style*="#F0EDE0"],table[bgcolor="#F0EDE0"],td[bgcolor="#F0EDE0"]{background-color:#203F7C!important}\n[style*="#F2F2F2"]{background-color:#1B2A58!important}\nh1,h2,h3,p,td,th,div,span,li,strong,b,img{color:#FFFFFF!important}\ntable.border thead th{border-bottom-color:#FFFFFF!important}\n.dm-only{display:block!important;max-height:none!important;overflow:visible!important}\n.dm-off{display:none!important}\na,a span{color:#FFFFFF!important}\ntable[bgcolor="#1B2A58"]:not([style*="border-radius"]),td[bgcolor="#1B2A58"]:not([style*="border-radius"]),[style*="background-color: #1B2A58"]:not([style*="border-radius"]),[style*="background-color:#1B2A58"]:not([style*="border-radius"]){background-color:#203F7C!important}\ntd[bgcolor="#1B2A58"][style*="border-radius"],a[style*="border-radius"][style*="#1B2A58"],a[style*="border-radius"][style*="rgb(27, 42, 88)"],td[style*="border-radius"][style*="rgb(27, 42, 88)"]{background-color:#005DAA!important}\n.dm-inv,.lnr-hero-img,img[src*="614ac005-c521-41d2-806a-6d8ec9f22a86"]{filter:brightness(0) invert(1)!important;-webkit-filter:brightness(0) invert(1)!important}\ntd[bgcolor="#005DAA"] a[style*="border-radius"],td[bgcolor="#005DAA"] table a[style*="border-radius"],a[style*="border-radius"][style*="background-color:#FFFFFF"],a[style*="border-radius"][style*="background-color: #FFFFFF"],a[style*="border-radius"][style*="background-color:#ffffff"]{background:#FEFEFE!important;background-color:#FEFEFE!important;color:#005DAA!important}\n.keep-cobalt,td[bgcolor="#005DAA"] a[style*="border-radius"] span,td[bgcolor="#FFFFFF"][style*="border-radius"] a,td[bgcolor="#FFFFFF"][style*="border-radius"] a span{color:#005DAA!important}\ntd[bgcolor="#FFFFFF"][style*="border-radius"]{background:#FEFEFE!important;background-color:#FEFEFE!important}\n}\n</style><!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]--><!--[if gte mso 16]><style>.keep-white{mso-style-textfill-type:gradient;mso-style-textfill-fill-gradientfill-stoplist:"0 \\#FFFFFF 0 100000\\,100000 \\#FFFFFF 0 100000";color:#000000 !important;}\n.keep-cobalt{mso-style-textfill-type:gradient;mso-style-textfill-fill-gradientfill-stoplist:"0 \\#005DAA 1 100000\\,99000 \\#005DAA 1 100000";color:#ffffff !important;}</style><![endif]-->';

/**
 * A type with no registered renderer emits NOTHING rather than throwing.
 *
 * ECH blocks (`sfmc_<contentId>`) register only once /sfmc-blocks resolves, so a
 * document restored ahead of that — a saved build, a favorite, a duplicated job —
 * can hold a type the registry does not know yet. Before this guard the whole
 * export and the whole canvas went down on it (caught by
 * views/StudioView.ech.test.tsx). Skipping the row means a not-yet-loaded block is
 * briefly absent from the preview; it renders as soon as SFMC answers.
 */
/**
 * THE MISSING-BLOCK PLACEHOLDER IS CANVAS FURNITURE. IT MUST NOT SHIP (2026-08-20).
 *
 * `echDoc` resolves a template placement to Layne's real block or — when that block
 * isn't in the pack — a placeholder that names it and its ContentID. That is right on
 * the canvas: it tells the requester exactly which block is missing instead of drawing
 * a lookalike (the defect Jack caught, where `ECH_BlueHeader` rendered as our
 * `eyebrow` block's gold "COMING SOON").
 *
 * But the canvas and the export go through the SAME `render()`, so without this the
 * dashed grey "Content Builder block · ECH_BlueHeader" box would be pasted into
 * Content Builder and sent to customers. Trading a disguised block for visible debug
 * furniture in a live email is not a fix.
 *
 * So the export drops the row entirely, and `validateEmail` raises a warning naming
 * the block — dropped silently would be its own trap.
 */
const IS_PLACEHOLDER = (type: string): boolean => type === "echblock";

/**
 * A FEATURED CARD THAT REPEATS AN EARLIER ONE IS A SPARE. 2026-08-29.
 *
 * Measured on the live dev build (JAX → Granary Park → "Find Your Dream Home"):
 * the template ships THREE Featured Plan blocks, the community resolved to ONE
 * plan, and the canvas rendered Halle II, Halle II again, and a third card still
 * showing Layne's declared placeholder. Jack: "yes spare dropped."
 *
 * THE RULE IS "NO TWO CARDS SHOW THE SAME PLAN", not "drop unfilled cards". A
 * repeat is unambiguous — the same floorplan twice in one email is never intended,
 * and the requester cannot fix it from the panel because both cards are correctly
 * showing the only plan there is. The FIRST card of any plan always survives, so a
 * single unfilled card stays exactly where it is and `requiredBlanks` stops it
 * shipping blank; deleting that one would be this file taking a decision away from
 * the requester. Note the rule does bite two UNFILLED cards, since both carry the
 * same declared placeholder name — which is the same fault and the same answer.
 *
 * KEYED ON `PlanName`, not on the block type: it is the field the duplicate is
 * visible in, it is what a reader would notice, and it needs no import from the
 * ECH layer (document.ts sits under it). A block with no PlanName is not a
 * Featured card and is never considered.
 *
 * NOT SILENT — same rule as the missing-block drop above: `duplicateCards()`
 * names them for `validateEmail`, because a block vanishing with no explanation is
 * the complaint Layne raised about the amenity block on 2026-08-28.
 */
const planNameOf = (b: AnyBlockInstance): string =>
  String((b && b.data && (b.data as Record<string, unknown>).PlanName) || "").trim();

export function duplicateCards(blocks: AnyBlockInstance[]): string[] {
  const seen: Record<string, 1> = {};
  const dupes: string[] = [];
  (blocks || []).forEach((b) => {
    const n = planNameOf(b);
    if (!n) return;
    const key = n.toLowerCase();
    if (seen[key]) { if (dupes.indexOf(n) < 0) dupes.push(n); return; }
    seen[key] = 1;
  });
  return dupes;
}

/**
 * The document minus the repeat cards. Pure and exported so it can be tested
 * without standing up the block registry — the same separation `missingBlocks`
 * has, and the reason the first version of its test asserted on rendered HTML
 * and proved nothing (a fabricated block type rendered "" and every assertion
 * failed for the wrong reason).
 */
export function dropDuplicateCards(blocks: AnyBlockInstance[]): AnyBlockInstance[] {
  const seen: Record<string, 1> = {};
  return (blocks || []).filter((b) => {
    const n = planNameOf(b).toLowerCase();
    if (!n) return true;                   /* not a Featured card */
    if (seen[n]) return false;             /* the same plan, already shown above */
    seen[n] = 1;
    return true;
  });
}

export function renderRows(blocks: AnyBlockInstance[]): string {
  return dropDuplicateCards(blocks)
    .filter((b) => !IS_PLACEHOLDER(b.type))
    .map((b) => {
      const def = FULL_REGISTRY[b.type];
      return def ? (def.render as (d: unknown) => string)(b.data) : "";
    })
    .join("\n");
}

/**
 * Which template placements could not be resolved to a real SFMC block.
 *
 * Read off the document rather than recomputed, so it reflects what is actually on the
 * canvas. Exported for the submit gate and for `validateEmail`'s warning.
 */
export function missingBlocks(blocks: AnyBlockInstance[]): string[] {
  return (blocks || [])
    .filter((b) => b && IS_PLACEHOLDER(b.type))
    .map((b) => String((b.data && b.data.name) || (b.__ech && b.__ech.name) || "an SFMC block"));
}

/**
 * Assemble the full document. When SFMC provides the blank template — the
 * `ECH_Blank_Template` Content Builder block (asset 1029913, Test - Jack Blocks /
 * templates; see services/sfmcBlocks.ts findShell) — it IS the document shell:
 * head, Outlook fixes, 600 wrapper and locked footer all come from SFMC. The rows
 * drop into its `<!--@SLOT-->` marker (or the SFMC template slot `<div
 * data-type="slot" …>`, or before `</body>`). Without a shell, the built-in
 * esExport head is the fallback — byte-for-byte the monolith's. (monolith
 * esExport, 2026-08-06)
 *
 * THE SHELL IS THE STOCK SFMC TEMPLATE PLUS THIS FILE'S HEAD (2026-09-15).
 *
 * Justin Bradford, reviewing Jie's Litmus tests: "a very large chunk of CSS missing
 * compared to the regular build in SFMC … classes on these modules … not referenced
 * in the CSS at all." Measured: a regular SFMC build is a templatebasedemail on
 * `Blank_NewBranding_Borderless_2025` (asset 980673) whose head carries ~21 KB of
 * stock CSS — Lennar typography, `.responsive-td` / `.container` mobile stacking,
 * `.modtitle` / `.contcopy`, legal-link hardening. Lauren copied that template into
 * the blocks folder on 2026-08-06 (`TEST_EmailCreativeStudio_Blank_template`) for
 * exactly this hook, but it is a `template` asset and the blocks pull returns
 * blocks, so `findShell` never saw it and every export shipped the 3.5 KB fallback
 * head below — Gmail, which drops body `<style>`, then lost the Listing Table's
 * borders and the blocks' mobile rules. `ECH_Blank_Template` (was `ECH_Shell`) is
 * now the stock template head + `<title>` + HEAD + the block CSS lifted from the
 * live blocks, as an htmlblock the pull does return. Layne or Lauren can edit the
 * stock part in Content Builder without a deploy; the part after the
 * `ECH_HEAD` comment mirrors HEAD and moves with it.
 *
 * SAFETY, because a shell can now arrive from Content Builder edits: a shell whose
 * head carries no `color-scheme` meta gets HEAD injected before `</head>` (dark mode
 * must never depend on someone remembering to paste it), one without a `<title>`
 * gets ours, and an `<html>` without `lang` gets `lang="en"`. A shell that already
 * carries them is left byte-for-byte alone.
 */
/**
 * OPEN TRACKING IS NOT OPTIONAL ON A PASTED EMAIL (2026-08-20).
 *
 * Source: Uche's `email-html-builder` → `lessons/RENDERING-FIXES.md` (2026-07-20),
 * verified against our own export the same day — a block-assembled document carried
 * no tracking tag at all.
 *
 * SFMC injects the open pixel for TEMPLATE-based emails but NOT for paste-HTML, and
 * paste-HTML is how a Studio build reaches Content Builder today: the specialist copies
 * this export in. Content Builder even warns "This email does not include the code to
 * track opens" — and if nobody reads the warning, the send reports zero opens forever,
 * which looks like a performance story rather than a missing tag.
 *
 * There is no business-unit toggle; tracking IDs are applied at send time and the only
 * suppression is the per-subscriber DoNotTrack attribute. So the tag goes into every
 * whole document, immediately after `<body>` — unless the shell already carries one
 * (Lauren's SFMC blank template does; never add a second).
 *
 * Kept in step with the monolith's `__stdOpenPixel` (index.html). If one moves, move both.
 */
/**
 * DARK MODE: TAG THE MONOCHROME IMAGES AT EXPORT, DO NOT TRUST THE SRC (2026-09-02).
 *
 * The dark rule flips known navy PNGs to white with a CSS filter. The first cut
 * matched them by `img[src*="<asset id>"]`. That held in the Litmus prototype (where
 * the images ALSO carried class="dm-inv") and failed on the first real export from
 * dev: Apple Mail 16 dark rendered the header logo Cobalt-on-navy. Apple Mail's
 * privacy proxy rewrites image URLs at render time, so a selector on `src` matches
 * nothing there — and Litmus's Apple Mail behaves the same. A class survives the
 * proxy, so the class is written into the markup HERE, at export, for every image
 * whose asset id is on the monochrome list — which also covers the DIVISION Contact
 * Us blocks (`/sfmc-isc`) that carry the house icon and can't be edited 47 times.
 * The `src` selector stays in the head as a belt for clients that don't proxy.
 *
 * The list is asset ids, not URLs: SFMC serves the same asset from several
 * `/lib/<member>/m/<n>/` paths. Hero art (all five `ECH_*Image` blocks, measured
 * 70–82% transparent + white + navy text) is matched by its own `lnr-hero-img`
 * class in the rule instead, and the amenity icons carry `dm-inv` in the
 * ECH_Amenities block itself (asset 1029215, 2026-09-02).
 */
export const DARK_MONO_ASSET_IDS = [
  "614ac005-c521-41d2-806a-6d8ec9f22a86", /* header logo (ECH_Logo) */
  "856cc67f-0c67-45ed-9908-fec81f810fe4", /* "We're Here to Help You" house icon (Contact Us default + division blocks) */
];
export function withDarkImageClasses(html: string): string {
  try {
    const h = String(html ?? "");
    return h.replace(/<img\b[^>]*>/gi, (tag: string): string => {
      if (!DARK_MONO_ASSET_IDS.some((id) => tag.indexOf(id) > -1)) return tag;
      if (/\bdm-inv\b/.test(tag)) return tag;
      const m = /\sclass="([^"]*)"/i.exec(tag);
      if (m) return tag.replace(m[0], ' class="' + (m[1] ? m[1] + " " : "") + 'dm-inv"');
      return tag.replace(/^<img\b/i, '<img class="dm-inv"');
    });
  } catch { return String(html ?? ""); }
}

export function withOpenPixel(html: string): string {
  try {
    const h = String(html ?? "");
    if (/<custom\s+name=["']opencounter["']/i.test(h)) return h;
    const m = /<body\b[^>]*>/i.exec(h);
    if (!m) return h; /* a fragment is not a document */
    const at = m.index + m[0].length;
    return h.slice(0, at) + '<custom name="opencounter" type="tracking" />' + h.slice(at);
  } catch { return String(html ?? ""); }
}

/** The SFMC template's own slot marker, when a raw template is handed over as the shell. */
const SFMC_SLOT_DIV = /<div\s+data-type="slot"[^>]*>\s*<\/div>/i;

/**
 * A SHELL A TEMPLATE NAMES IS USED AS-IS — THE APP ADDS NOTHING TO IT (2026-10-06).
 *
 * Layne, Teams 10-05/06: Justin's new shell (`ECH_V2_Shell`, a Content Builder
 * Template asset) runs alongside `ECH_Blank_Template`, and each template says which
 * shell it uses (`"shell"` in its `@GUARDRAILS`). The direction for that shell is
 * "everything in the email lives in SFMC, the app just assembles it", so for a shell
 * resolved that way the export does NOT:
 *   - inject HEAD / <title> / lang (`completeShell`) — the shell is light-only on
 *     purpose (`color-scheme: light`, no dark-mode rules);
 *   - wrap the rows in the fixed 600px `es-w` table — it would undo the shell's
 *     fluid 100% / 600px-max frame. `<!--@SLOT-->` sits inside the shell's own
 *     table, so the rows go straight in;
 *   - add `dm-inv` to the monochrome images (no dark CSS to drive it).
 * It still adds the open pixel ONLY when the shell has none: a paste-HTML send gets
 * no SFMC-injected pixel (see withOpenPixel), and zero opens is the silent failure.
 * Each block keeps its `<tr><td>` wrap (Justin's modules start as tables).
 *
 * Strings compare by value, so the set holds the resolved (balance-gated) markup the
 * ECH layer hands to `renderDocument` — `services/echSnap.ts` `echShellFor`.
 */
const BARE_SHELLS = new Set<string>();
export function markBareShell(html: string): void { if (html) BARE_SHELLS.add(html); }
export function isBareShell(html: string | null | undefined): boolean { return !!html && BARE_SHELLS.has(html); }
/** test seam */
export function __resetBareShells(): void { BARE_SHELLS.clear(); }

function renderIntoBareShell(sh: string, rows: string): string | null {
  if (sh.indexOf("<!--@SLOT-->") > -1) return withOpenPixel(sh.replace("<!--@SLOT-->", rows));
  /* no marker: the rows still need a table — a fluid one, never the fixed 600 */
  const fluid =
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse">\n' +
    rows +
    "\n</table>";
  if (SFMC_SLOT_DIV.test(sh)) return withOpenPixel(sh.replace(SFMC_SLOT_DIV, fluid));
  if (sh.indexOf("</body>") > -1) return withOpenPixel(sh.replace("</body>", fluid + "</body>"));
  return null;
}

/**
 * A shell's head must carry the dark-mode head. Content Builder edits can drop it
 * (the 08-22 ECH_Shell sat with one dark rule for three weeks), so the export checks
 * for the `color-scheme` meta and injects HEAD when it is absent. Title and `lang`
 * follow the same rule. Exported for the test.
 */
export function completeShell(shell: string): string {
  let sh = String(shell ?? "");
  /* The search runs on the shell with its HTML comments blanked (same length, so the
     indexes still map onto `sh`). The 09-16 ECH_HEAD comment in ECH_Blank_Template
     reads "…everything from here to </head> mirrors document.ts…" — the literal
     `</head>` inside that comment was the first match, so the title and HEAD were
     injected INSIDE the comment; HEAD's `<!--[if !mso]><!-->` then closed the comment
     early and " mirrors components/email-builder/… keep them in sync. -->" rendered as
     body text at the top of every Hub email (Jie's Litmus 22789875, SFMC 1034117),
     with a second copy of the dark head behind it. Markup inside a comment is prose. */
  const scan = sh.replace(/<!--[\s\S]*?-->/g, (c: string): string => " ".repeat(c.length));
  const headClose = scan.search(/<\/head>/i);
  if (headClose < 0) return sh; /* not a document with a head — leave it */
  const headPart = scan.slice(0, headClose);
  let add = "";
  if (!/<title[\s>]/i.test(headPart)) add += "<title>Lennar</title>";
  if (!/name="color-scheme"/i.test(headPart)) add += HEAD;
  if (add) sh = sh.slice(0, headClose) + add + sh.slice(headClose);
  sh = sh.replace(/<html\b(?![^>]*\blang=)([^>]*)>/i, '<html$1 lang="en">');
  return sh;
}

/**
 * THE DEFAULT SHELL COMES FROM THE ECH LAYER, WITHOUT THIS FILE IMPORTING IT (2026-09-15).
 *
 * Four call sites render the document — App (the form preview AND the submit's HTML),
 * StudioView's export, the template fill (`useEchTemplates`) and the live doc fill —
 * and three of them called `renderDocument(blocks)` with no shell, so the SFMC shell
 * reached the Studio's copy button and nothing a requester actually sends. Rather
 * than thread the shell through every caller (and miss the next one), the ECH layer
 * registers a provider once (`services/echSnap.ts`, where the adopted blocks live);
 * `undefined` means "the shell in hand", an explicit `null` means "no shell" (tests
 * of the fallback head pass `null` or run with no provider registered).
 */
let shellProvider: (() => string | null) | null = null;
export function setShellProvider(fn: (() => string | null) | null): void { shellProvider = fn; }
export function defaultShell(): string | null {
  try { return shellProvider ? shellProvider() : null; } catch { return null; }
}

export function renderDocument(blocks: AnyBlockInstance[], shell?: string | null): string {
  const rows = renderRows(blocks);
  const chosen = shell === undefined ? defaultShell() : shell;
  if (chosen && isBareShell(chosen)) {
    const out = renderIntoBareShell(chosen, rows);
    if (out !== null) return out;
  }
  if (chosen) {
    const sh = completeShell(chosen);
    /* `es-w` so the head's `@media (max-width:600px){.es-w{width:100%!important}}`
       collapses it on a phone — measured at 375px without it: the stock `.container`
       went 100% but this fixed-600 table forced the document to 600 and a sideways
       scroll (2026-09-15, test asset 1033964). Same class the fallback wrapper carries. */
    const tbl =
      '<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center" class="es-w" style="width:600px;max-width:600px;border-collapse:collapse">\n' +
      rows +
      "\n</table>";
    if (sh.indexOf("<!--@SLOT-->") > -1) return withOpenPixel(withDarkImageClasses(sh.replace("<!--@SLOT-->", tbl)));
    if (SFMC_SLOT_DIV.test(sh)) return withOpenPixel(withDarkImageClasses(sh.replace(SFMC_SLOT_DIV, tbl)));
    if (sh.indexOf("</body>") > -1) return withOpenPixel(withDarkImageClasses(sh.replace("</body>", tbl + "</body>")));
  }
  return withOpenPixel(withDarkImageClasses(
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">\n<html xmlns="http://www.w3.org/1999/xhtml" lang="en" xml:lang="en"><head>' +
    /* lang + <title>: Jie's 9/1 test send (Litmus 22637220) flagged the two WCAG 2A
       "Serious" failures a screen reader hits first - no document language and an
       empty title. Both live on this exported shell and nowhere else. The title is
       deliberately generic: the export has no subject in scope, and clients show the
       subject rather than the title, so a stale per-request title would be worse. */
    "<title>Lennar</title>" +
    HEAD +
    "</head>\n" +
    '<body class="es-bg" style="margin:0;padding:0;background:#ffffff">\n' +
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#FFFFFF" class="es-bg"><tr><td align="center">\n' +
    '<!--[if mso | IE]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td><![endif]-->\n' +
    '<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center" class="es-w" style="width:600px;max-width:600px;border-collapse:collapse">\n' +
    rows +
    "\n</table>\n" +
    "<!--[if mso | IE]></td></tr></table><![endif]-->\n" +
    "</td></tr></table>\n</body></html>"
  ));
}

/**
 * Export QA — ported from legacy esValidate (Gmail 102KB clip, alt text,
 * unsubscribe, CAN-SPAM address, Outlook flex/grid, script strip).
 */
export interface ValidationResult {
  warnings: string[];
  advisories: string[];
  kb: number;
}

export function validateEmail(html: string, blocks?: AnyBlockInstance[]): ValidationResult {
  const w: string[] = [];
  const adv: string[] = [];
  /* A DROPPED ROW MUST BE SAID OUT LOUD. `renderRows` removes the missing-block
     placeholder so debug furniture can't reach a customer — but a block silently
     absent from the email is its own defect, so the export names it here. */
  const miss = missingBlocks(blocks || []);
  if (miss.length) {
    w.push(
      miss.length === 1
        ? miss[0] + " isn't in Marketing Cloud, so it was left out of the email — ask eMarketing for it before sending."
        : miss.length + " blocks aren't in Marketing Cloud and were left out (" + miss.join(", ") + ") — ask eMarketing before sending.",
    );
  }
  const bytes = new TextEncoder().encode(html).length;
  const kb = Math.round(bytes / 1024);
  const low = html.toLowerCase();
  if (bytes > 102000) w.push("Over Gmail's 102KB clip (" + kb + "KB) — Gmail will truncate");
  else if (bytes > 80000) adv.push("Heavy (" + kb + "KB) — aim under 80KB");
  const imgs = html.match(/<img\b[^>]*>/gi) || [];
  const noAlt = imgs.filter((t) => !/\balt\s*=\s*["'][^"']+["']/i.test(t));
  if (noAlt.length) w.push(noAlt.length + " image" + (noAlt.length > 1 ? "s" : "") + " missing alt text");
  if (/display\s*:\s*(flex|grid)/i.test(low)) w.push("Uses flex/grid — breaks in Outlook (Windows)");
  if (/<script/i.test(low)) w.push("Contains <script> — email clients strip it");
  if (!/unsubscribe/i.test(low)) w.push("No unsubscribe link — add a Footer block");
  if (!/corporation|suite|blvd|avenue|\b[a-z]{2}\s+\d{5}\b/i.test(html)) w.push("No physical address (CAN-SPAM)");
  if (low.indexOf("prefers-color-scheme") < 0) adv.push("No dark-mode CSS");
  return { warnings: w, advisories: adv, kb };
}
